import React, { ReactNode } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";

import { StyledBottomAppBar } from "./shared/styles";
import Button from "../../components/button/Button";

export interface BottomAppBarButtonProps {
  /** スクロール方向に応じてバーを隠すか（false で常時表示） */
  scrollEnabled?: boolean;
  /** 左側（戻る/キャンセル）ボタンのラベル。null で左ボタンを出さない */
  before?: string | null;
  /** 右側（次へ/保存）ボタンのラベル。null で右ボタンを出さない */
  after?: string | null;
  /** 右側ボタンの右アイコン（Material Symbols 名。例: "chevron_right"） */
  afterIcon?: string;
  /** 右側ボタンの左隣の自由スロット（合計金額の表示など） */
  children?: ReactNode;
  onClickBefore?: () => void;
  onClickAfter?: () => void;
  beforeDisabled?: boolean;
  afterDisabled?: boolean;
}

/**
 * 戻る（左下）/ 次へ（右下）の 2 ボタンを持つ Bottom App Bar。
 * ウィザードやエディタの確定操作用（ガラス風の画面下固定バー）。
 * `before` / `after` に null を渡すと片側だけのバーにできる。
 */
const BottomAppBarButton: React.FC<BottomAppBarButtonProps> = ({
  scrollEnabled = true,
  before = "戻る",
  after = "次へ",
  afterIcon,
  children,
  onClickBefore,
  onClickAfter,
  beforeDisabled = false,
  afterDisabled = false,
}) => {
  // スクロール方向に応じてバーを上下させる（BottomAppBarProject と同じ挙動）
  const scrollState = useSelector((state: { layout: string }) => state.layout);
  const bottomAppBarBottom = !scrollEnabled
    ? 0
    : scrollState === "down"
      ? -80
      : 0;

  // 祖先の transform / filter に fixed 配置が捕捉されないよう body へポータルする
  // （Modal と同じ方針。ずれ・ボタン欠けを防ぐ）。
  return createPortal(
    <StyledBottomAppBar $bottomAppBarBottom={bottomAppBarBottom}>
      {/* ボタン列は本文（.u-wrapper = min(100% - 32px, 1140px)）と同じ幅上限で中央寄せ */}
      <div className="u-wrapper u-row-between u-gap16 u-align-center">
        <div>
          {before !== null && (
            <Button
              size="medium"
              theme="tertiary"
              onClick={onClickBefore}
              disabled={beforeDisabled}
            >
              {before}
            </Button>
          )}
        </div>
        <div className="u-row u-gap16 u-align-center">
          {children}
          {after !== null && (
            <Button
              size="medium"
              theme="primary"
              rightIcon={afterIcon}
              onClick={onClickAfter}
              disabled={afterDisabled}
            >
              {after}
            </Button>
          )}
        </div>
      </div>
    </StyledBottomAppBar>,
    document.body,
  );
};

export default BottomAppBarButton;
